/**
 * Contratos de `Place` — el pin en el mapa.
 *
 * Un `Place` es un DATO FACTUAL: dónde está y cómo se llama. No es una
 * opinión, y por eso **crearlo no requiere Visit Token** (`docs/09` §0.1).
 * Publicar una `Recommendation` tampoco lo exige desde ADR-020: el token
 * pasó a ser un sello visible, no una puerta.
 *
 * Un `Place` sin ninguna recomendación no tiene reputación, no rankea y no
 * aparece en descubrimiento: es solo un pin.
 */

import { z } from 'zod';

export const PLACE_NAME_MIN = 2;
export const PLACE_NAME_MAX = 80;

/**
 * Perfil de geofence (`docs/09` §4.1). No es una categoría de producto:
 * determina radio y permanencia, porque el GPS se comporta de forma
 * radicalmente distinta en una banqueta y en un tercer piso de plaza.
 */
export const PlaceKind = z.enum(['standard', 'indoor', 'street', 'large_venue']);
export type PlaceKind = z.infer<typeof PlaceKind>;

/** Qué le decimos al usuario que significa cada perfil. */
export const PLACE_KIND_LABEL: Record<PlaceKind, string> = {
  standard: 'Local con puerta a la calle',
  street: 'Puesto de calle o esquina',
  indoor: 'Dentro de una plaza o mercado techado',
  large_venue: 'Mercado grande o corredor',
};

export const PlaceName = z.string().trim().min(PLACE_NAME_MIN).max(PLACE_NAME_MAX);

/**
 * Coordenadas. El rango se valida aquí y NO solo en el servidor: mandar
 * una latitud imposible y esperar el 400 es un viaje perdido en 4G.
 */
export const Coordinates = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type Coordinates = z.infer<typeof Coordinates>;

export const MAX_FOOD_TYPES_PER_PLACE = 3;

export const CreatePlaceInput = z.object({
  name: PlaceName,
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  kind: PlaceKind.default('standard'),
  /**
   * Opcionales de verdad. Pedir dirección obligatoria mataría el caso que
   * más importa a este producto: los mejores tacos de la ciudad no tienen
   * dirección exacta (`docs/09` §4.2).
   */
  address: z.string().trim().max(200).optional(),
  foodTypes: z.array(z.string().trim().min(1)).max(MAX_FOOD_TYPES_PER_PLACE).default([]),
});
export type CreatePlaceInput = z.infer<typeof CreatePlaceInput>;

/**
 * Distancia por debajo de la cual dos pines se consideran el mismo lugar.
 *
 * 40 m y no 120: aquí no se trata del geofence, sino de que dos personas
 * no creen "El Vilsito" dos veces. En un corredor denso hay puestos
 * distintos a 50 m, y fusionarlos sería peor que duplicar — un duplicado
 * se limpia, un lugar fusionado por error pierde las recomendaciones de
 * alguien.
 */
export const DUPLICATE_RADIUS_M = 40;

export const PlaceError = z.enum([
  'invalid_place',
  'place_duplicate',
  'unauthenticated',
]);
export type PlaceError = z.infer<typeof PlaceError>;
